import {useApolloClient} from '@apollo/client';
import {createStyles} from '@mantine/core';
import React, {useCallback, useMemo, useState} from 'react';

import AIMenu from 'llm/chat-library/ui/AIMenu';
import {
  REDESIGN_DRAWER_BACKGROUND,
  REDESIGN_HEADER_ICON_CHIP_BACKGROUND,
} from 'llm/chat-library/ui/AIStyleConstants';
import {RealtimeMessageType} from 'realtime/Constants';
import useRealtimeChannelSubscription from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForApiId} from 'realtime/util/RealtimeUtil';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

import {ChatHistoryItemIcon, ChatSidepanelItem} from '../ChatListing';
import {getChatNameRenderInfo} from '../ChatNameUtil';
import type {SidepanelChat} from '../ChatRouter';
import {type ChatSessionStarUpdate, useChatSessionMenuOptions} from '../ChatSessionMenu';
import {updateChatSessionInCache} from '../RenameChatModal';
import SidebarRow from './SidebarRow';

const useStyles = createStyles((_, {showDots, isActive}: {showDots: boolean; isActive: boolean}) => ({
  dots: {
    visibility: showDots ? 'visible' : 'hidden',
    display: 'flex',
    alignItems: 'center',
    // Opaque chip matching the row background so the floating "…" button cleanly masks the text tail
    // it overlays (the label now spans the full row width).
    paddingLeft: 8,
    backgroundColor: isActive ? REDESIGN_HEADER_ICON_CHIP_BACKGROUND : REDESIGN_DRAWER_BACKGROUND,
  },
}));

export interface RedesignChatRowProps {
  item: ChatSidepanelItem;
  onStarUpdated: (update: ChatSessionStarUpdate) => Promise<void>;
  onSelect: (chat: SidepanelChat) => void;
  isSelected: boolean;
}

/**
 * A single Starred/Automated/Recent row in the redesigned chat sidebar. Built on the shared
 * {@link SidebarRow} so it aligns with the nav items, with a dimmed icon. There is no inline star —
 * starring lives in the hover overflow ("…") menu alongside Rename, matching the mock.
 */
export default function RedesignChatRow({
  item,
  onStarUpdated,
  onSelect,
  isSelected,
}: RedesignChatRowProps): JSX.Element {
  const [isHovering, setIsHovering] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {classes} = useStyles({
    showDots: item.id != null && (isHovering || isMenuOpen),
    isActive: isSelected,
  });
  const apolloClient = useApolloClient();
  const chatNameInfo = getChatNameRenderInfo(item);

  const menuOptions = useChatSessionMenuOptions({
    chatSession: {
      id: item.id ?? '',
      name: item.name,
      star: item.star,
    },
    onStarUpdated,
  });

  const onChatNameUpdated = useCallback(
    (message: {data: {name: string; modifiedAt: string}}) => {
      if (item.id != null) {
        updateChatSessionInCache(apolloClient, item.id, message.data.name, message.data.modifiedAt);
      }
    },
    [apolloClient, item.id]
  );

  useRealtimeChannelSubscription(
    item.id != null ? getRealtimeChannelNameForApiId(item.id) : '',
    onChatNameUpdated,
    [RealtimeMessageType.CHAT_NAME_UPDATED]
  );

  const menu = useMemo(
    () => (
      <AIMenu scrollable={false} height={MenuHeight.SMALL} width={MenuWidth.FIT_CONTENT} dropLeft={true}>
        <Section>
          {menuOptions.map((option, index) => (
            <Option
              key={index}
              icon={option.icon}
              isDisabled={option.isDisabled}
              onSelect={() => {
                option.onSelect?.();
                setIsMenuOpen(false);
              }}
            >
              {option.contents}
            </Option>
          ))}
        </Section>
      </AIMenu>
    ),
    [menuOptions]
  );

  const dots =
    item.id != null ? (
      <span
        className={classes.dots}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <AbsoluteDropdownContainer
          menu={menu}
          open={isMenuOpen}
          onRequestClose={() => {
            setIsMenuOpen(false);
          }}
          shouldCloseOnInsideClick={true}
          shouldCloseOnOutsideClick={true}
        >
          <Button
            aria-label="Chat options"
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            buttonStyle={ButtonStyle.TRANSPARENT}
            icon="ellipsis-vertical"
            size={ButtonSize.MINI}
            onClick={() => {
              setIsMenuOpen(true);
            }}
          />
        </AbsoluteDropdownContainer>
      </span>
    ) : undefined;

  return (
    <SidebarRow
      icon={<ChatHistoryItemIcon item={item} />}
      label={chatNameInfo.node}
      tooltipLabel={chatNameInfo.text}
      dimIcon={true}
      active={isSelected}
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        setIsHovering(false);
      }}
      onClick={() => {
        onSelect(item.id != null ? {id: item.id, type: item.type} : {id: null});
      }}
      trailing={dots}
      dataTestAttrs={{
        component: 'RedesignChatRow',
        element: 'root',
        key: item.name ?? '',
        ...(isSelected ? {index: 'active'} : {}),
      }}
    />
  );
}
